/**
 * Muscle Mapping Utility
 * 
 * Provides mapping between exercise names and their corresponding muscle groups
 * for use with the MuscleHighlighter component.
 * 
 * Features:
 * - Exercise to muscle group mapping
 * - Primary and secondary muscle identification
 * - Standardized muscle group names
 * - Easy extension for new exercises
 */

// Standardized muscle group names that match SVG element IDs
export const MUSCLE_GROUPS = {
  // Upper Body
  CHEST: 'chest',
  SHOULDER: 'shoulder',
  BICEPS: 'biceps',
  TRICEPS: 'triceps',
  FOREARMS: 'forearms',
  
  // Core
  ABS: 'abs',
  OBLIQUE: 'oblique',
  RECTUS_ABDOMINIS: 'rectus_abdominis',
  
  // Back
  BACK: 'back',
  LATS: 'lats',
  TRAPS: 'traps',
  LOWER_BACK: 'lower_back',
  
  // Lower Body
  QUADRICEPS: 'quadriceps',
  HAMSTRING: 'hamstring',
  GLUTES: 'glutes',
  ABDUCTOR: 'abductor',
  CALVES: 'calves',
  
  // Other
  KNEE: 'knee',
  NECK: 'neck',
  NECK_CENTER: 'neck_center'
};

// Exercise to muscle mapping
export const EXERCISE_MUSCLE_MAP = {
  // Chest Exercises
  'Push-ups': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.TRICEPS, MUSCLE_GROUPS.SHOULDER]
  },
  'Bench Press': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.TRICEPS, MUSCLE_GROUPS.SHOULDER]
  },
  'Incline Bench Press': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.TRICEPS]
  },
  'Incline Dumbbell Press': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.TRICEPS]
  },
  'Medicine Ball Chest Pass': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.TRICEPS]
  },
  'Decline Bench Press': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.TRICEPS]
  },
  'Chest Flyes': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.SHOULDER]
  },
  'Dips': {
    primary: [MUSCLE_GROUPS.CHEST, MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.SHOULDER]
  },

  // Back Exercises
  'Pull-ups': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BACK],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.TRAPS]
  },
  'Chin-ups': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BICEPS],
    secondary: [MUSCLE_GROUPS.BACK, MUSCLE_GROUPS.TRAPS]
  },
  'Lat Pulldowns': {
    primary: [MUSCLE_GROUPS.LATS],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.BACK]
  },
  'Seated Cable Rows': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BACK, MUSCLE_GROUPS.TRAPS],
    secondary: [MUSCLE_GROUPS.BICEPS]
  },
  'Bent-over Rows': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BACK],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.TRAPS]
  },
  'Bent-Over Barbell Rows': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BACK],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.TRAPS]
  },
  'Barbell Rows': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BACK],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.TRAPS]
  },
  'Weighted Pull-ups': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BACK],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.TRAPS]
  },
  'Explosive Pull-ups': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BACK],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.TRAPS]
  },
  'Deadlifts': {
    primary: [MUSCLE_GROUPS.LOWER_BACK, MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.TRAPS, MUSCLE_GROUPS.FOREARMS]
  },
  'Conventional Deadlifts': {
    primary: [MUSCLE_GROUPS.LOWER_BACK, MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.TRAPS, MUSCLE_GROUPS.FOREARMS]
  },
  'T-Bar Rows': {
    primary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.BACK],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.TRAPS]
  },

  // Shoulder Exercises
  'Shoulder Press': {
    primary: [MUSCLE_GROUPS.SHOULDER],
    secondary: [MUSCLE_GROUPS.TRICEPS]
  },
  'Overhead Press (Standing)': {
    primary: [MUSCLE_GROUPS.SHOULDER],
    secondary: [MUSCLE_GROUPS.TRICEPS, MUSCLE_GROUPS.ABS]
  },
  'Push Press': {
    primary: [MUSCLE_GROUPS.SHOULDER],
    secondary: [MUSCLE_GROUPS.TRICEPS, MUSCLE_GROUPS.QUADRICEPS]
  },
  'Battle Ropes': {
    primary: [MUSCLE_GROUPS.SHOULDER],
    secondary: [MUSCLE_GROUPS.ABS, MUSCLE_GROUPS.TRAPS]
  },
  'Lateral Raises': {
    primary: [MUSCLE_GROUPS.SHOULDER],
    secondary: []
  },
  'Rear Delt Flyes': {
    primary: [MUSCLE_GROUPS.SHOULDER],
    secondary: [MUSCLE_GROUPS.BACK]
  },
  'Front Raises': {
    primary: [MUSCLE_GROUPS.SHOULDER],
    secondary: []
  },
  'Upright Rows': {
    primary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.TRAPS],
    secondary: [MUSCLE_GROUPS.BICEPS]
  },

  // Arm Exercises
  'Bicep Curls': {
    primary: [MUSCLE_GROUPS.BICEPS],
    secondary: [MUSCLE_GROUPS.FOREARMS]
  },
  'Hammer Curls': {
    primary: [MUSCLE_GROUPS.BICEPS],
    secondary: [MUSCLE_GROUPS.FOREARMS]
  },
  'Tricep Extensions': {
    primary: [MUSCLE_GROUPS.TRICEPS],
    secondary: []
  },
  'Tricep Dips': {
    primary: [MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.CHEST]
  },
  'Close-grip Bench Press': {
    primary: [MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.CHEST]
  },
  'Close-Grip Bench Press': {
    primary: [MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.CHEST]
  },
  'Weighted Dips': {
    primary: [MUSCLE_GROUPS.CHEST, MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.SHOULDER]
  },

  // Leg Exercises
  'Squats': {
    primary: [MUSCLE_GROUPS.QUADRICEPS, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.CALVES]
  },
  'Barbell Back Squats': {
    primary: [MUSCLE_GROUPS.QUADRICEPS, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.CALVES]
  },
  'Front Squats': {
    primary: [MUSCLE_GROUPS.QUADRICEPS, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.ABS]
  },
  'Jump Squats': {
    primary: [MUSCLE_GROUPS.QUADRICEPS, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.CALVES, MUSCLE_GROUPS.ABS]
  },
  'Box Jumps': {
    primary: [MUSCLE_GROUPS.QUADRICEPS, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.CALVES, MUSCLE_GROUPS.ABS]
  },
  'Lunges': {
    primary: [MUSCLE_GROUPS.QUADRICEPS, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.CALVES]
  },
  'Leg Press': {
    primary: [MUSCLE_GROUPS.QUADRICEPS],
    secondary: [MUSCLE_GROUPS.GLUTES]
  },
  'Leg Curls': {
    primary: [MUSCLE_GROUPS.HAMSTRING],
    secondary: [MUSCLE_GROUPS.CALVES]
  },
  'Leg Extensions': {
    primary: [MUSCLE_GROUPS.QUADRICEPS],
    secondary: []
  },
  'Romanian Deadlifts': {
    primary: [MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.LOWER_BACK]
  },
  'Calf Raises': {
    primary: [MUSCLE_GROUPS.CALVES],
    secondary: []
  },
  'Hip Thrusts': {
    primary: [MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.HAMSTRING]
  },

  // Core Exercises
  'Crunches': {
    primary: [MUSCLE_GROUPS.ABS],
    secondary: []
  },
  'Sit-ups': {
    primary: [MUSCLE_GROUPS.ABS],
    secondary: []
  },
  'Planks': {
    primary: [MUSCLE_GROUPS.ABS],
    secondary: [MUSCLE_GROUPS.LOWER_BACK, MUSCLE_GROUPS.SHOULDER]
  },
  'Plank Variations': {
    primary: [MUSCLE_GROUPS.ABS],
    secondary: [MUSCLE_GROUPS.LOWER_BACK, MUSCLE_GROUPS.SHOULDER]
  },
  'Russian Twists': {
    primary: [MUSCLE_GROUPS.OBLIQUE],
    secondary: [MUSCLE_GROUPS.ABS]
  },
  'Mountain Climbers': {
    primary: [MUSCLE_GROUPS.ABS],
    secondary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.QUADRICEPS]
  },
  'Leg Raises': {
    primary: [MUSCLE_GROUPS.ABS],
    secondary: []
  },
  'Side Planks': {
    primary: [MUSCLE_GROUPS.OBLIQUE],
    secondary: [MUSCLE_GROUPS.ABS]
  },

  // Compound Exercises
  'Burpees': {
    primary: [MUSCLE_GROUPS.CHEST, MUSCLE_GROUPS.QUADRICEPS],
    secondary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.TRICEPS, MUSCLE_GROUPS.ABS]
  },
  'Thrusters': {
    primary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.QUADRICEPS],
    secondary: [MUSCLE_GROUPS.TRICEPS, MUSCLE_GROUPS.GLUTES]
  },
  'Clean and Press': {
    primary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.TRAPS],
    secondary: [MUSCLE_GROUPS.QUADRICEPS, MUSCLE_GROUPS.GLUTES, MUSCLE_GROUPS.LATS]
  },
  'Kettlebell Swings': {
    primary: [MUSCLE_GROUPS.GLUTES, MUSCLE_GROUPS.HAMSTRING],
    secondary: [MUSCLE_GROUPS.ABS, MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.TRAPS]
  },
  'Barbell Bench Press': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.TRICEPS, MUSCLE_GROUPS.SHOULDER]
  },
  'Cable Flyes (Mid-chest)': {
    primary: [MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.SHOULDER]
  },
  'Overhead Triceps Extension': {
    primary: [MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.SHOULDER]
  },
  'Diamond Push-ups': {
    primary: [MUSCLE_GROUPS.TRICEPS, MUSCLE_GROUPS.CHEST],
    secondary: [MUSCLE_GROUPS.SHOULDER]
  },
  'Cable Triceps Pushdowns': {
    primary: [MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.FOREARMS]
  },
  'Deadlifts (Conventional)': {
    primary: [MUSCLE_GROUPS.LOWER_BACK, MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.LATS, MUSCLE_GROUPS.TRAPS, MUSCLE_GROUPS.FOREARMS]
  },
  'Wide-Grip Pull-ups/Lat Pulldowns': {
    primary: [MUSCLE_GROUPS.LATS],
    secondary: [MUSCLE_GROUPS.BICEPS, MUSCLE_GROUPS.BACK]
  },
  'Face Pulls': {
    primary: [MUSCLE_GROUPS.SHOULDER, MUSCLE_GROUPS.TRAPS],
    secondary: [MUSCLE_GROUPS.BACK]
  },
  'Barbell Biceps Curls': {
    primary: [MUSCLE_GROUPS.BICEPS],
    secondary: [MUSCLE_GROUPS.FOREARMS]
  },
  'Cable Biceps Curls': {
    primary: [MUSCLE_GROUPS.BICEPS],
    secondary: [MUSCLE_GROUPS.FOREARMS]
  },
  'Farmer\'s Walks': {
    primary: [MUSCLE_GROUPS.FOREARMS, MUSCLE_GROUPS.TRAPS],
    secondary: [MUSCLE_GROUPS.ABS, MUSCLE_GROUPS.BACK]
  },
  'Bulgarian Split Squats': {
    primary: [MUSCLE_GROUPS.QUADRICEPS, MUSCLE_GROUPS.GLUTES],
    secondary: [MUSCLE_GROUPS.HAMSTRING, MUSCLE_GROUPS.CALVES]
  },
  'Hanging Leg Raises': {
    primary: [MUSCLE_GROUPS.ABS],
    secondary: [MUSCLE_GROUPS.OBLIQUE]
  },
  'Dead Bug': {
    primary: [MUSCLE_GROUPS.ABS],
    secondary: [MUSCLE_GROUPS.LOWER_BACK]
  },
  'Arnold Press': {
    primary: [MUSCLE_GROUPS.SHOULDER],
    secondary: [MUSCLE_GROUPS.TRICEPS]
  },
  'Barbell Curls (21s)': {
    primary: [MUSCLE_GROUPS.BICEPS],
    secondary: [MUSCLE_GROUPS.FOREARMS]
  },
  'Close-Grip Push-ups': {
    primary: [MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.CHEST, MUSCLE_GROUPS.SHOULDER]
  },
  'Alternating Dumbbell Curls': {
    primary: [MUSCLE_GROUPS.BICEPS],
    secondary: [MUSCLE_GROUPS.FOREARMS]
  },
  'Triceps Dips': {
    primary: [MUSCLE_GROUPS.TRICEPS],
    secondary: [MUSCLE_GROUPS.CHEST]
  },
  'Wrist Curls': {
    primary: [MUSCLE_GROUPS.FOREARMS],
    secondary: [MUSCLE_GROUPS.BICEPS]
  },
  'Reverse Wrist Curls': {
    primary: [MUSCLE_GROUPS.FOREARMS],
    secondary: [MUSCLE_GROUPS.BICEPS]
  }
};

/**
 * Get muscle groups for a given exercise
 * @param {string} exerciseName - Name of the exercise
 * @returns {Object} Object containing primary and secondary muscle arrays
 */
export const getMusclesForExercise = (exerciseName) => {
  const exerciseData = EXERCISE_MUSCLE_MAP[exerciseName];
  if (!exerciseData) {
    console.warn(`No muscle mapping found for exercise: ${exerciseName}`);
    return { primary: [], secondary: [] };
  }
  return exerciseData;
};

/**
 * Get all unique muscle groups from an array of exercises
 * @param {Array} exercises - Array of exercise names
 * @returns {Object} Object containing combined primary and secondary muscles
 */
export const getMusclesForExercises = (exercises) => {
  const allPrimary = new Set();
  const allSecondary = new Set();
  
  exercises.forEach(exerciseName => {
    const { primary, secondary } = getMusclesForExercise(exerciseName);
    primary.forEach(muscle => allPrimary.add(muscle));
    secondary.forEach(muscle => allSecondary.add(muscle));
  });
  
  return {
    primary: Array.from(allPrimary),
    secondary: Array.from(allSecondary)
  };
};

/**
 * Check if an exercise exists in the mapping
 * @param {string} exerciseName - Name of the exercise
 * @returns {boolean} True if exercise exists in mapping
 */
export const hasExerciseMapping = (exerciseName) => {
  return exerciseName in EXERCISE_MUSCLE_MAP;
};

/**
 * Get all available exercise names
 * @returns {Array} Array of all exercise names in the mapping
 */
export const getAllExerciseNames = () => {
  return Object.keys(EXERCISE_MUSCLE_MAP);
};

/**
 * Add or update exercise mapping
 * @param {string} exerciseName - Name of the exercise
 * @param {Array} primaryMuscles - Array of primary muscle groups
 * @param {Array} secondaryMuscles - Array of secondary muscle groups
 */
export const addExerciseMapping = (exerciseName, primaryMuscles = [], secondaryMuscles = []) => {
  EXERCISE_MUSCLE_MAP[exerciseName] = {
    primary: primaryMuscles,
    secondary: secondaryMuscles
  };
};

export default {
  MUSCLE_GROUPS,
  EXERCISE_MUSCLE_MAP,
  getMusclesForExercise,
  getMusclesForExercises,
  hasExerciseMapping,
  getAllExerciseNames,
  addExerciseMapping
};